import type { View } from "@/types/board";

const STORAGE_KEY = "board-views";
const MAX_BOARDS_PER_WORKSPACE = 25;

type BoardViews = Record<string, string>;
type WorkspaceBoardViews = Record<string, BoardViews>;

function readAll(): WorkspaceBoardViews {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function readStoredBoardViewId(workspaceId: string | null | undefined, boardId: string): string | null {
  if (!workspaceId) return null;
  const viewId = readAll()[workspaceId]?.[boardId];
  return typeof viewId === "string" ? viewId : null;
}

/** Re-inserting moves the board to the end; past the cap the oldest (first) key is dropped. */
export function saveStoredBoardViewId(workspaceId: string | null | undefined, boardId: string, viewId: string): void {
  if (typeof window === "undefined" || !workspaceId || !viewId) return;
  const all = readAll();
  const { [boardId]: _, ...rest } = all[workspaceId] ?? {};
  const boards: BoardViews = { ...rest, [boardId]: viewId };
  const keys = Object.keys(boards);
  for (const key of keys.slice(0, Math.max(0, keys.length - MAX_BOARDS_PER_WORKSPACE))) delete boards[key];
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [workspaceId]: boards }));
  } catch {
    // Storage full or blocked — the view still works, it just won't survive a reload.
  }
}

/** The view a board opens on: the last one used on this device if it still exists, else the first standalone view. */
export function pickDefaultView(views: View[] | undefined, boardId: string, workspaceId: string | null | undefined): View | undefined {
  if (!views?.length) return undefined;
  const storedId = readStoredBoardViewId(workspaceId, boardId);
  return (
    (storedId && views.find((v) => String(v._id) === storedId && !v.pinnedToDashboard)) ||
    views.find((v) => !v.pinnedToDashboard) ||
    views[0]
  );
}
